// ABOUTME: Describes admin dashboard field labels, tooltips, and applicability.
import { isPersistableConfigField } from '../../../types/adminConfigPatch';
import { isPersistablePollingField } from '../../../types/adminPollingPatch';
import { Destination, EntityType, Source } from '../../../types/enums';
import {
  IntegrationPath,
  integrationPath,
  matchesIntegrationPath,
  matchesPollingOrigin,
  parseIntegrationPath,
  parseSource,
} from '../../../types/integrationPath';

export type FieldType = 'toggle' | 'text' | 'number' | 'select' | 'multiselect';

export interface FieldDescription {
  label: string;
  description: string;
  type: FieldType;
  /** Product intent: show as an input in the admin config form. */
  editable: boolean;
  /** Paths where this field applies. Omitted = genuinely universal. */
  integrationPaths?: IntegrationPath[];
  /**
   * Entity types this field affects within its path(s). Omitted = applies to
   * every entity on those paths. Present = the field only renders when the
   * config's entity is one of these, so a multi-entity flow (e.g. TechLog →
   * AMOS covers defects, flights and uplifts) does not surface a field that
   * has no effect for the config's entity.
   */
  entities?: EntityType[];
  /**
   * Polling-item origin + entity scoping. Used instead of integrationPaths
   * when a field's entity applicability differs per origin (e.g. registrations
   * on Traxxall defects but not workplanner). Omitted entities = all entities
   * for that origin.
   */
  pollingOrigins?: Array<{ origin: Source; entities?: EntityType[] }>;
  /**
   * Another field in the same config whose value must match before this field
   * has any runtime effect. When unmet, the field renders disabled (never
   * hidden) with a note pointing at the gate. Omitted = always active.
   */
  enabledWhen?: { field: string; equals: string | boolean };
  defaultValue?: string;
  applicableFor: 'polling' | 'config' | 'both';
  // Fixed choices. Required for 'select'. For 'multiselect', omit to allow
  // free-form values (e.g. aircraft registrations); supply to constrain choices.
  options?: string[];
  // Inclusive numeric bounds for 'number' fields. Mirror the backend PATCH
  // schema so the input cannot offer a value the backend would reject.
  min?: number;
  max?: number;
  step?: number;
  /** When true, the drawer rejects empty/whitespace values before save. */
  required?: boolean;
}

// A registry field paired with the config property key it maps to.
export interface RegistryField extends FieldDescription {
  key: string;
}

export const FIELD_DESCRIPTIONS: Record<string, FieldDescription> = {
  // ==================== SHARED FIELDS (BOTH POLLING & CONFIG) ====================

  id: {
    label: 'Document ID',
    description: 'Unique identifier for this document in Firestore.',
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  origin: {
    label: 'Origin',
    description: 'Source system being polled for data.',
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  entity: {
    label: 'Entity Type',
    description:
      'Type of data being synchronized (FLIGHTS, DEFECTS, WORKPACKAGES, USERS, TOTALS, etc).',
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  originOperatorId: {
    label: 'Origin Operator ID',
    description: 'Unique identifier for the operator in the source system.',
    type: 'text',
    editable: true,
    required: true,
    applicableFor: 'both',
  },

  originUserId: {
    label: 'Origin User ID',
    description: 'API user ID used to authenticate with the source system.',
    type: 'text',
    editable: true,
    required: true,
    applicableFor: 'both',
  },

  url: {
    label: 'API URL',
    description:
      'API endpoint URL for the source system. On AMOS polling items this is the unique identifier used to match destination configs.',
    integrationPaths: [integrationPath(Source.AMOS, Destination.TECHLOG)],
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  isDisabled: {
    label: 'Disabled',
    description:
      'When true, this polling item or configuration will be skipped during sync workflows.',
    type: 'toggle',
    editable: false,
    applicableFor: 'both',
  },

  createdAt: {
    label: 'Created At',
    description: 'Timestamp when this document was first created.',
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  updatedAt: {
    label: 'Updated At',
    description: 'Timestamp when this document was last modified.',
    type: 'text',
    editable: false,
    applicableFor: 'both',
  },

  customRegistrationCode: {
    label: 'Custom Registration Code',
    description:
      'Optional prefix appended to aircraft registrations before looking them up in the destination system.',
    integrationPaths: [integrationPath(Source.AMOS, Destination.TECHLOG)],
    entities: [EntityType.WORKPACKAGE],
    pollingOrigins: [
      { origin: Source.AMOS, entities: [EntityType.WORKPACKAGE] },
    ],
    type: 'text',
    editable: true,
    applicableFor: 'both',
  },

  // ==================== POLLING-ONLY FIELDS ====================

  lastSync: {
    label: 'Last Sync',
    description:
      'Timestamp of the most recent successful polling cycle for this integration.',
    type: 'text',
    editable: false,
    applicableFor: 'polling',
  },

  configsCount: {
    label: 'Configs Count',
    description:
      'Number of destination configurations associated with this polling item. Calculated dynamically, not stored.',
    type: 'number',
    editable: false,
    applicableFor: 'polling',
  },

  operatorName: {
    label: 'Operator Name',
    description:
      'Techlog operator display name denormalized onto the polling item for the admin dashboard.',
    type: 'text',
    editable: false,
    applicableFor: 'polling',
  },

  simplifiedMaintenanceItem: {
    label: 'Simplified Maintenance',
    description:
      'When enabled, filters AMOS workpackages to only include reduced maintenance records before sending to Techlog.',
    pollingOrigins: [
      { origin: Source.AMOS, entities: [EntityType.WORKPACKAGE] },
    ],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'polling',
  },

  registrations: {
    label: 'Aircraft Registrations',
    description:
      'On polling items: comma-separated aircraft registration codes that limit which aircraft are synced. On Rusada configs: per-aircraft registration map (edited through per-registration configuration, not this form).',
    type: 'multiselect',
    editable: true,
    applicableFor: 'polling',
    pollingOrigins: [
      {
        origin: Source.TRAXXALL,
        entities: [EntityType.DEFECT, EntityType.DUE_LIST],
      },
      {
        origin: Source.CAMP,
        entities: [
          EntityType.DEFECT,
          EntityType.DUE_LIST,
          EntityType.WORKPACKAGE,
        ],
      },
      { origin: Source.ASTRO, entities: [EntityType.FLIGHT] },
      { origin: Source.RAAS, entities: [EntityType.MXITEM] },
      { origin: Source.TECHLOG, entities: [EntityType.TOTALS] },
    ],
  },

  groupFlights: {
    label: 'Group Flights',
    description:
      'When enabled, Techlog flight polling batches same-day sectors into a single AMOS export group. Legacy path; grouped sync is not fully wired.',
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'polling',
    pollingOrigins: [{ origin: Source.TECHLOG, entities: [EntityType.FLIGHT] }],
  },

  qualifications: {
    label: 'Required Qualifications',
    description:
      'Qualification type IDs an AMOS user must hold to be synced to Techlog. Only users matching at least one listed qualification are exported.',
    type: 'multiselect',
    editable: true,
    applicableFor: 'polling',
    pollingOrigins: [{ origin: Source.AMOS, entities: [EntityType.USERS] }],
  },

  userPermissions: {
    label: 'User Permissions',
    description:
      'Nested permission map. On AMOS user-sync polling it pairs with qualifications to filter exported users. Not editable from the flat admin form.',
    type: 'text',
    editable: false,
    applicableFor: 'both',
    pollingOrigins: [{ origin: Source.AMOS, entities: [EntityType.USERS] }],
  },

  daysToFetch: {
    label: 'Days To Fetch',
    description:
      'How many days into the future to fetch flights during polling (1–89). Controls the upper bound of the sync window for Leon and FL3XX flight polling.',
    type: 'number',
    editable: true,
    defaultValue: '89',
    min: 1,
    max: 89,
    step: 1,
    applicableFor: 'polling',
    pollingOrigins: [
      { origin: Source.LEON, entities: [EntityType.FLIGHT] },
      { origin: Source.FL3XX, entities: [EntityType.FLIGHT] },
    ],
  },

  filterRegistrations: {
    label: 'Filter Registrations',
    description:
      'When enabled, Leon flight polling derives its aircraft list from configured Techlog aircraft instead of syncing every registration.',
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'polling',
    pollingOrigins: [{ origin: Source.LEON, entities: [EntityType.FLIGHT] }],
  },

  // ==================== CONFIG-ONLY FIELDS ====================

  destination: {
    label: 'Destination',
    description: 'Target system where synchronized data will be sent.',
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  destinationOperatorId: {
    label: 'Destination Operator ID',
    description:
      'Unique identifier for the operator in the destination system.',
    type: 'text',
    editable: true,
    required: true,
    applicableFor: 'config',
  },

  destinationUserId: {
    label: 'Destination User ID',
    description:
      'API user ID used to authenticate with the destination system.',
    type: 'text',
    editable: true,
    required: true,
    applicableFor: 'config',
  },

  syncCount: {
    label: 'Sync Count',
    description:
      'Total number of successful synchronizations performed with this configuration. Calculated dynamically, not stored.',
    type: 'number',
    editable: false,
    applicableFor: 'config',
  },

  lastSyncAt: {
    label: 'Last Sync At',
    description:
      'Timestamp of the most recent successful sync using this configuration. Calculated dynamically, not stored.',
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  // ==================== TECHLOG → AMOS INTEGRATION FLAGS ====================

  useATASpec: {
    label: 'Use ATA Spec',
    description:
      'When enabled, exports data using ATA Spec2000 Chapter 17 XML format instead of simplified JSON payloads. Disable only when the destination AMOS system cannot process ATA Chapter 17.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT, EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'true',
    applicableFor: 'config',
  },

  useICAOCode: {
    label: 'Use ICAO Codes',
    description:
      'Controls airport code format. When enabled, uses 4-letter ICAO codes (e.g., KJFK); when disabled, uses 3-letter IATA codes (e.g., JFK).',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT, EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'true',
    applicableFor: 'config',
  },

  hideComments: {
    label: 'Hide Comments',
    description:
      'When enabled, excludes user-generated comments from defect exports. Use when the operator does not want TrustFlight-originated notes in AMOS.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  disableDeferrals: {
    label: 'Disable Deferrals',
    description:
      'Forces all defects to be exported as informational records instead of active deferrals, preventing AMOS from creating live deferment records.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  useThreePartAtaFormat: {
    label: 'Three-Part ATA Format',
    description:
      'When enabled, ATA codes are formatted with all three parts (chapter-section-subject) instead of two parts (chapter-section).',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  includeSubsubsection: {
    label: 'Include Subsubsection',
    description:
      'Appends the subsubsection code (CDL/MEL/NEF) to defect document numbers, providing more granular ATA reference information.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  ignoreInspectionComplete: {
    label: 'Ignore Inspection Complete',
    description:
      'Skips exporting inspection completion maintenance actions. Use when the operator handles inspection closures directly in AMOS.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'true',
    applicableFor: 'config',
  },

  ignoreInspectionCompleteSignature: {
    label: 'Ignore Inspection Signature',
    description:
      'Suppresses engineer signature data for inspection completion actions. Typically used together with Ignore Inspection Complete.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  useResolutionAirport: {
    label: 'Use Resolution Airport',
    description:
      'Uses the airport where the defect was resolved instead of where it was reported. Only applicable when resolution airport is captured.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  operatorShortName: {
    label: 'Operator Short Name',
    description:
      'Abbreviated operator name included in exported data for identification purposes.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.FLIGHT],
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  issueSignee: {
    label: 'Issue Signee',
    description:
      'Default signatory name used for defect records when not using ATA Spec format. Required for non-ATA exports.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: false },
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  amosUserSign: {
    label: 'AMOS User Sign',
    description:
      'User signature identifier for AMOS system authentication and audit trails.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT, EntityType.UPLIFT],
    type: 'text',
    editable: true,
    defaultValue: 'ELB_Sign',
    applicableFor: 'config',
  },

  ataChapterDefault: {
    label: 'Default ATA Chapter',
    description:
      'Fallback ATA chapter code used when defects have no chapter specified.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    type: 'text',
    editable: true,
    defaultValue: '99',
    applicableFor: 'config',
  },

  ataSectionDefault: {
    label: 'Default ATA Section',
    description:
      'Fallback ATA section code used when defects have no section specified.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    type: 'text',
    editable: true,
    defaultValue: '99',
    applicableFor: 'config',
  },

  ataSubjectDefault: {
    label: 'Default ATA Subject',
    description:
      'Fallback ATA subject code used when defects have no subject specified.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  iataCode: {
    label: 'Default IATA Code',
    description:
      'Default airport code used when location information is missing from flight or defect records.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT, EntityType.UPLIFT],
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  // ==================== LEON → TECHLOG INTEGRATION FLAGS ====================

  groupByDate: {
    label: 'Group By Date (Legacy)',
    description:
      'Legacy flag that groups all Leon flights on the same day into a single Techlog trip. Read-only — use Creation Method instead. Saving a Creation Method edit keeps this flag aligned automatically.',
    integrationPaths: [integrationPath(Source.LEON, Destination.TECHLOG)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: false,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  creationMethod: {
    label: 'Creation Method',
    description:
      'Controls how flights are organized into Techlog trips: "standard" (one trip for all flights), "groupByDate" (one trip per day), or "1trip:1flight" (separate trip for each flight).',
    integrationPaths: [
      integrationPath(Source.LEON, Destination.TECHLOG),
      integrationPath(Source.FL3XX, Destination.TECHLOG),
    ],
    entities: [EntityType.FLIGHT],
    type: 'select',
    editable: true,
    defaultValue: 'standard',
    applicableFor: 'config',
    options: ['standard', 'groupByDate', '1trip:1flight'],
  },

  addCallsign: {
    label: 'Add Callsign',
    description:
      'When enabled, includes the aircraft callsign from Leon in the Techlog trip data.',
    integrationPaths: [integrationPath(Source.LEON, Destination.TECHLOG)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  useV2: {
    label: 'Use V2 API',
    description:
      'Routes synchronization through the V2 API endpoints and transformation pipeline. Leave disabled to use legacy V1 workflows.',
    integrationPaths: [integrationPath(Source.LEON, Destination.TECHLOG)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  // ==================== TECHLOG → LEON INTEGRATION FLAGS ====================

  closeJourneyLog: {
    label: 'Close Journey Log',
    description:
      'Controls whether journey logs are marked as "closed" or left "open" in Leon. When closed, local crews cannot continue editing.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.LEON)],
    entities: [EntityType.FLIGHT],
    type: 'select',
    editable: true,
    defaultValue: 'open',
    applicableFor: 'config',
    options: ['open', 'closed'],
  },

  defaultFuelUpliftToZero: {
    label: 'Default Fuel To Zero',
    description:
      'Ensures fuel uplift values are never negative by converting missing or negative values to zero.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.LEON)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  useTruckActualForFuelUplift: {
    label: 'Use Truck Actual',
    description:
      'Uses the fuel truck reported delivery amount instead of calculated uplift. Falls back to calculated value when truck data is unavailable.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.LEON)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  skipUnlinkedTechlogFlights: {
    label: 'Skip Unlinked Techlog Flights',
    description:
      'When enabled, Techlog flights without an existing Leon sync link are not created in Leon. Flights that already have a Leon link continue to update.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.LEON)],
    entities: [EntityType.FLIGHT],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  // ==================== AMOS → TECHLOG INTEGRATION FLAGS ====================

  convertWorkpackageToTask: {
    label: 'Convert To Tasks',
    description:
      'When enabled, AMOS workpackages are converted into individual Techlog tasks instead of grouped workpackage records.',
    integrationPaths: [integrationPath(Source.AMOS, Destination.TECHLOG)],
    entities: [EntityType.WORKPACKAGE],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  useReleaseDate: {
    label: 'Use Release Date',
    description:
      'Uses the release record date instead of its creation date for defect release timestamps in ATA exports to AMOS.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.DEFECT],
    enabledWhen: { field: 'useATASpec', equals: true },
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  // ==================== TRANSFORMATION ENGINE FLAGS ====================

  schema: {
    label: 'Transformation Schema',
    description:
      'Custom JSON schema defining field mappings and transformation rules. When provided, overrides default transformation logic. Managed through the Schema Mapper, not the config form.',
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  useSchema: {
    label: 'Enable Transmormer',
    description:
      'Config-level Transmormer switch that no runtime workflow reads; the live engine selector is the polling item "useTransmormer" flag. Kept for documentation only, not editable from the config form.',
    type: 'toggle',
    editable: false,
    applicableFor: 'config',
  },

  // ==================== AIRCRAFT & FILTERING FLAGS ====================

  preflightCustomFields: {
    label: 'Preflight Custom Fields',
    description:
      'Array of custom field identifiers to include in preflight data exports from Techlog.',
    integrationPaths: [integrationPath(Source.LEON, Destination.TECHLOG)],
    entities: [EntityType.FLIGHT],
    type: 'multiselect',
    editable: true,
    applicableFor: 'config',
    options: ['flight_id', 'flight_type', 'flight_icao_type'],
  },

  aircraftRegistration: {
    label: 'Aircraft Registration',
    description:
      'Single aircraft registration code. When set, limits Navblue flight polling to only this aircraft.',
    integrationPaths: [integrationPath(Source.NAVBLUE, Destination.TECHLOG)],
    entities: [EntityType.FLIGHT],
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  aircraftRegistrations: {
    label: 'Aircraft Registrations (Legacy)',
    description:
      'Deprecated array of aircraft registration codes. Replaced by the per-registration `registrations` map for Rusada configs. Not editable from the admin panel config form.',
    type: 'multiselect',
    editable: false,
    applicableFor: 'config',
  },

  aircraftIds: {
    label: 'Aircraft IDs',
    description:
      'Array of system-specific aircraft IDs. When set, limits uplift synchronization to only these aircraft.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.UPLIFT, EntityType.OIL_AND_FUEL],
    type: 'multiselect',
    editable: true,
    applicableFor: 'config',
  },

  fluidTypes: {
    label: 'Fluid Types',
    description:
      'Array of fuel/fluid type identifiers for filtering which uplifts are synchronized.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    entities: [EntityType.UPLIFT, EntityType.OIL_AND_FUEL],
    type: 'multiselect',
    editable: true,
    applicableFor: 'config',
  },

  // ==================== LEGACY/DEPRECATED FLAGS ====================

  useAIMModule: {
    label: 'Use AIM Module (Legacy)',
    description:
      'Legacy flag for AMOS Integration Module. No longer actively used.',
    integrationPaths: [integrationPath(Source.TECHLOG, Destination.AMOS)],
    type: 'toggle',
    editable: false,
    applicableFor: 'config',
  },

  useTransmormer: {
    label: 'Use Transmormer',
    description:
      'Polling-item engine selector: when enabled, routes synchronization through the Transmormer transformation pipeline instead of legacy adapters.',
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'polling',
  },

  useSyncRecordAdapter: {
    label: 'Use Sync Record Adapter',
    description:
      'When enabled, every legacy storage write also writes an enriched record to syncLogs in the same Firestore transaction. Reads stay on the legacy collection. Missing values are treated as off.',
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  destinationOprId: {
    label: 'Destination Operator ID (Legacy)',
    description:
      'Legacy field name. Replaced by "destinationOperatorId" in current implementations.',
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  // ==================== FL3XX INTEGRATION FLAGS ====================

  useStaging: {
    label: 'Use Staging',
    description:
      'Routes FL3XX API calls to the staging environment instead of production. Use for testing configurations before deploying to live operators.',
    integrationPaths: [
      integrationPath(Source.FL3XX, Destination.TECHLOG),
      integrationPath(Source.TECHLOG, Destination.FL3XX),
    ],
    type: 'toggle',
    editable: true,
    defaultValue: 'false',
    applicableFor: 'config',
  },

  baseAPIUrl: {
    label: 'Base API URL',
    description:
      'Custom FL3XX API endpoint URL. When set, overrides environment-based URL resolution. Use for self-hosted or non-standard deployments.',
    integrationPaths: [
      integrationPath(Source.FL3XX, Destination.TECHLOG),
      integrationPath(Source.TECHLOG, Destination.FL3XX),
    ],
    type: 'text',
    editable: true,
    applicableFor: 'config',
  },

  // ==================== TRAXXALL INTEGRATION FLAGS ====================

  apiKey: {
    label: 'API Key',
    description:
      'Traxxall API authentication key. Entered by the operator in the TechLog integration card; credential management is not performed from the admin panel.',
    integrationPaths: [
      integrationPath(Source.TRAXXALL, Destination.TECHLOG),
      integrationPath(Source.TECHLOG, Destination.TRAXXALL),
    ],
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  apiPassword: {
    label: 'API Password',
    description:
      'Traxxall API authentication password. Entered by the operator in the TechLog integration card; credential management is not performed from the admin panel.',
    integrationPaths: [
      integrationPath(Source.TRAXXALL, Destination.TECHLOG),
      integrationPath(Source.TECHLOG, Destination.TRAXXALL),
    ],
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },

  // ==================== VERYON TRACKING+ (RUSADA) INTEGRATION FLAGS ====================

  scheduledMxThresholds: {
    label: 'Scheduled MX Thresholds',
    description:
      'Per-aircraft thresholds (flight hours, days, engine cycles, total landings) that gate which Veryon Tracking+ scheduled maintenance items sync to Techlog. Stored as a nested object and edited through the per-registration configuration, not as a single form field.',
    integrationPaths: [integrationPath(Source.RUSADA, Destination.TECHLOG)],
    type: 'text',
    editable: false,
    applicableFor: 'config',
  },
};

/**
 * Get field description by key
 */
export function getFieldDescription(
  fieldKey: string,
): FieldDescription | undefined {
  return FIELD_DESCRIPTIONS[fieldKey];
}

/**
 * Format field name for display (converts camelCase to Title Case)
 */
export function formatFieldLabel(fieldKey: string): string {
  const description = FIELD_DESCRIPTIONS[fieldKey];
  if (description?.label) {
    return description.label;
  }

  // Fallback: Convert camelCase to Title Case
  return fieldKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Get editable config fields for a specific integration path, each paired with
 * its config property key so a form can read and write the right property.
 * Fields with no integrationPaths are treated as genuinely universal once the
 * path is validated.
 */
export function getEditableFieldsForPath(
  path: IntegrationPath,
  entity?: string,
): RegistryField[] {
  return Object.entries(FIELD_DESCRIPTIONS)
    .filter(([, field]) => {
      if (!field.editable) return false;

      // Config tab only edits config fields
      if (field.applicableFor !== 'config' && field.applicableFor !== 'both') {
        return false;
      }

      // A field scoped to specific entities only applies to a config for one of
      // those entities. Omitted entities means it applies to every entity on
      // the path. Without a known entity the path-level set is returned intact.
      if (
        entity &&
        field.entities &&
        !field.entities.includes(entity as EntityType)
      ) {
        return false;
      }

      // No integrationPaths means the field applies to every valid path
      if (!field.integrationPaths || field.integrationPaths.length === 0) {
        return true;
      }

      return field.integrationPaths.some((fieldPath) =>
        matchesIntegrationPath(fieldPath, path),
      );
    })
    .map(([key, field]) => ({ key, ...field }));
}

/**
 * Parse config origin/destination strings once, then return editable fields.
 * Invalid enum values throw instead of silently falling back to universal fields.
 */
export function getEditableFieldsForConfig(
  origin: string,
  destination: string,
  entity?: string,
): RegistryField[] {
  return getEditableFieldsForPath(
    parseIntegrationPath(origin, destination),
    entity,
  );
}

/**
 * Config form fields that are both catalog-editable and accepted by
 * PATCH /v2/api/admin/configs/:id. The config form renders from this set so
 * every input can save. A test keeps the editable catalog aligned with the
 * PATCH map.
 */
export function getPersistableEditableFieldsForPath(
  path: IntegrationPath,
  entity?: string,
): RegistryField[] {
  return getEditableFieldsForPath(path, entity).filter((field) =>
    isPersistableConfigField(field.key),
  );
}

export function getPersistableEditableFieldsForConfig(
  origin: string,
  destination: string,
  entity?: string,
): RegistryField[] {
  return getPersistableEditableFieldsForPath(
    parseIntegrationPath(origin, destination),
    entity,
  );
}

function matchesPollingScope(
  field: FieldDescription,
  origin: Source,
  entity?: string,
): boolean {
  if (field.pollingOrigins?.length) {
    const scope = field.pollingOrigins.find((entry) => entry.origin === origin);
    if (!scope) {
      return false;
    }
    if (
      entity &&
      scope.entities &&
      !scope.entities.includes(entity as EntityType)
    ) {
      return false;
    }
    return true;
  }

  if (field.integrationPaths?.length) {
    if (
      !field.integrationPaths.some((path) => matchesPollingOrigin(path, origin))
    ) {
      return false;
    }
    if (
      entity &&
      field.entities &&
      !field.entities.includes(entity as EntityType)
    ) {
      return false;
    }
    return true;
  }

  return true;
}

/**
 * Editable polling fields for a source origin and entity. Polling items have no
 * destination; integrationPaths on a field are matched by origin only unless
 * pollingOrigins provides per-origin entity scoping.
 */
export function getEditableFieldsForPolling(
  origin: string,
  entity?: string,
): RegistryField[] {
  const parsedOrigin = parseSource(origin);

  return Object.entries(FIELD_DESCRIPTIONS)
    .filter(([key, field]) => {
      if (!field.editable) return false;
      if (field.applicableFor !== 'polling' && field.applicableFor !== 'both') {
        return false;
      }
      // Disabled state is toggled from the grid, not the edit drawer (SS-1161).
      if (key === 'isDisabled') return false;
      return matchesPollingScope(field, parsedOrigin, entity);
    })
    .map(([key, field]) => ({ key, ...field }));
}

export function getPersistableEditableFieldsForPolling(
  origin: string,
  entity?: string,
): RegistryField[] {
  return getEditableFieldsForPolling(origin, entity).filter((field) =>
    isPersistablePollingField(field.key),
  );
}

export { isPersistableConfigField, isPersistablePollingField };
